/**
 *
 * Signup
 *
 */

import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import { compose } from 'redux';
import PropTypes from 'prop-types';

import injectSaga from 'utils/injectSaga';
import injectReducer from 'utils/injectReducer';
import SignupImg from 'images/PC-Sign.svg';
import bbdSignUpImage from 'images/pc-bbd-sign.png';
import pcEapLogo from 'images/pc-eap-eng-logo.png';
import pcEapLogoFr from 'images/pc-eap-fr-logo.png';
import BBDLogo from 'images/BBDLogo.png';
import BBDLogoFr from 'images/BBDLogoFr.png';
import FrenchLogo from 'images/PC-PeopleCare-Logos_FR.svg';
import H2 from 'components/H2';
import SVG from 'react-inlinesvg';
import Logo from 'images/PC-Nav.svg';
import { tabletSize, phoneSize } from 'global-styles';
import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import { BBDBAP, BBDEAP } from 'utils/environment';
import pcCarepathBapLogoFr from 'images/pcCarePathBapFr.png';
import pcCarepathBapLogo from 'images/pcCarePathBap.png';
import SignupForm from './SignupForm';
import {
  makeSelectSignupPage,
  makeSelectStatuses,
  makeSelectUSCompanies,
} from './selectors';
import { fetchStatuses, fetchUSCompanies } from './actions';
import reducer from './reducer';
import saga from './saga';
import messages from './messages';

const SignupWrapper = styled.div`
  display: flex;
  max-width: 905px;
  min-height: 740px;
  background-color: #ffffff;
  z-index: 1;
  position: relative;
  margin: 70px auto;
  box-shadow: 0 0 50px 0 rgba(40, 71, 140, 0.2);

  @media (max-width: ${tabletSize}) {
    width: 90%;
  }
`;

const InfoContainer = styled.div`
  padding: 40px;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  width: 100%;

  h2 {
    margin-bottom: 40px;
  }

  @media (max-width: ${tabletSize}) {
    padding-bottom: 200px;
    margin: 0 auto;
    width: 70%;

    h2 {
      text-align: center;
    }
  }

  @media (max-width: ${phoneSize}) {
    padding-bottom: 150px;
    width: 100%;
  }

  @media (max-width: 360px) {
    zoom: 60%;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 58%;

  @media (max-width: ${tabletSize}) {
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  margin: 40px;
  margin-right: 0;

  @media (max-width: ${tabletSize}) {
    margin-bottom: 100px;
  }

  @media (max-width: ${phoneSize}) {
    margin-bottom: 80px;
  }
`;

const ImageWrapper = styled.div`
  width: 42%;
  background-color: #f6f8fa;

  img {
    width: 100%;
    margin-left: -30px;
    margin-top: 50%;
  }

  @media (max-width: ${tabletSize}) {
    display: none;
  }
`;

const BBDLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export class Signup extends React.PureComponent {
  componentDidMount() {
    this.props.fetchStatuses();
    this.props.fetchUSCompanies();
  }

  renderLogo = locale => {
    if (BBDBAP && locale === 'fr') {
      return (
        <BBDLogoWrapper>
          <img style={{ width: '250px' }} src={BBDLogoFr} alt="bbd logo" />
          <img
            style={{ width: '250px' }}
            src={pcCarepathBapLogoFr}
            alt="bbd carepath bap logo"
          />
        </BBDLogoWrapper>
      );
    }
    if (BBDBAP) {
      return (
        <BBDLogoWrapper>
          <img style={{ width: '250px' }} src={BBDLogo} alt="bbd logo" />
          <img
            style={{ width: '250px' }}
            src={pcCarepathBapLogo}
            alt="bbd carepath bap logo"
          />
        </BBDLogoWrapper>
      );
    }
    if (BBDEAP && locale === 'fr') {
      return (
        <BBDLogoWrapper>
          <img style={{ width: '250px' }} src={BBDLogoFr} alt="bbd logo" />
          <img style={{ width: '250px' }} src={pcEapLogoFr} alt="pc eap logo" />
        </BBDLogoWrapper>
      );
    }
    if (BBDEAP) {
      return (
        <BBDLogoWrapper>
          <img style={{ width: '250px' }} src={BBDLogo} alt="bbd logo" />
          <img style={{ width: '250px' }} src={pcEapLogo} alt="pc eap logo" />
        </BBDLogoWrapper>
      );
    }
    if (locale === 'fr') {
      return <SVG style={{ width: '250px' }} src={FrenchLogo} alt="logo" />;
    }
    return <SVG style={{ width: '250px' }} src={Logo} alt="logo" />;
  };

  renderPlanName = () => {
    if (BBDBAP) {
      return <FormattedMessage {...messages.bbdBap} />;
    }
    if (BBDEAP) {
      return <FormattedMessage {...messages.bbdEap} />;
    }
    return <FormattedMessage {...messages.peopleCare} />;
  };

  isAbbreviatedSignup = () => {
    const path = window.location.pathname;
    return path.includes('/unlink');
  };

  render() {
    const { statuses, locale, usCompanies } = this.props;

    return (
      <div>
        <Helmet>
          <title>Signup</title>
          <meta name="description" content="People Corp Signup" />
        </Helmet>
        <SignupWrapper id="signup">
          <LeftContainer>
            <Header>{this.renderLogo(locale)}</Header>
            <InfoContainer>
              <H2>
                <FormattedMessage
                  {...messages.welcome}
                  values={{
                    bold: (
                      <span style={{ fontWeight: '600' }}>
                        {this.renderPlanName()}
                      </span>
                    ),
                  }}
                />
              </H2>
              <SignupForm
                statuses={statuses.map(status => {
                  const newStatus = status.label
                    .toLowerCase()
                    .replace(/\s/g, '')
                    .replace('/', '');
                  return {
                    value: status.value,
                    label: <FormattedMessage {...messages[newStatus]} />,
                  };
                })}
                usCompanies={usCompanies}
                isAbbreviatedSignup={this.isAbbreviatedSignup()}
              />
            </InfoContainer>
          </LeftContainer>
          <ImageWrapper>
            {BBDBAP || BBDEAP ? (
              <img src={bbdSignUpImage} alt="signup" />
            ) : (
              <img src={SignupImg} alt="signup" />
            )}
          </ImageWrapper>
        </SignupWrapper>
      </div>
    );
  }
}

Signup.propTypes = {
  fetchStatuses: PropTypes.func.isRequired,
  statuses: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  usCompanies: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  signup: makeSelectSignupPage(),
  statuses: makeSelectStatuses(),
  usCompanies: makeSelectUSCompanies(),
  locale: makeSelectLocale(),
});

function mapDispatchToProps(dispatch) {
  return {
    fetchStatuses: () => dispatch(fetchStatuses()),
    fetchUSCompanies: () => dispatch(fetchUSCompanies()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withReducer = injectReducer({ key: 'signup', reducer });
const withSaga = injectSaga({ key: 'signup', saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(Signup);
